import type { AnalyticsStore } from '@mission.io/mission-player'
import {
	getAnalyticsOverview,
	type AnalyticsOverview,
	type BothFullMissionStates,
} from '@mission.io/mission-toolkit'
import { AWARD_TYPES } from '@mission.io/mission-toolkit/constants'
import { getQuestionPercentage } from '../../../context/analyticsOverview'
import { getTopScorers } from '../../../context/missionState/endOfMission'

const ONE_SECOND = 1000 // ms

// question score count up durations
export const QUESTION_COUNT_UP_DELAY = ONE_SECOND
const QUESTION_COUNT_UP_TIME_MAX = 5 * ONE_SECOND

// top students appearing durations
export const TOP_STUDENT_DURATION_PER_STUDENT = ONE_SECOND
export const TOP_STUDENT_EXPANDING_DURATION = ONE_SECOND

// mission score count up durations
export const SCORE_DELAY_LENGTH = ONE_SECOND
export const SCORE_LENGTH_OF_SOUND = 7500
export const SCORE_SLIDE_DURATION = ONE_SECOND
export const SCORE_AVERAGE_FADE_IN_DURATION = ONE_SECOND
export const SCORE_HEIGHT_CHANGE_DURATION = ONE_SECOND

// leaderboard positioning durations
export const LEADERBOARD_EXPANDING_DURATION = ONE_SECOND
export const LEADERBOARD_SCROLL_DURATION = 4 * ONE_SECOND
export const LEADERBOARD_SCORE_FADE_IN_DURATION = ONE_SECOND
export const LEADERBOARD_PLACEMENT_DURATION = 3 * ONE_SECOND

// Page durations
export const PAGE_FADE_DURATION = ONE_SECOND
export const BEFORE_FADE_OUT_DURATION = 5 * ONE_SECOND
const PAGE_DURATION = PAGE_FADE_DURATION * 2 + BEFORE_FADE_OUT_DURATION

// duration of leaderboard page
const LEADERBOARD_PAGE_DURATION =
	PAGE_DURATION +
	// score animation durations
	SCORE_DELAY_LENGTH +
	SCORE_LENGTH_OF_SOUND +
	SCORE_SLIDE_DURATION +
	SCORE_AVERAGE_FADE_IN_DURATION +
	SCORE_HEIGHT_CHANGE_DURATION +
	// leaderboard animation durations
	LEADERBOARD_EXPANDING_DURATION +
	LEADERBOARD_SCROLL_DURATION +
	LEADERBOARD_SCORE_FADE_IN_DURATION +
	LEADERBOARD_PLACEMENT_DURATION

// Average Class Health Page
export const HEALTH_COUNT_UP_DURATION = 5 * ONE_SECOND
export const POINTS_PING_DURATION = 2 * ONE_SECOND

/**
 * getQuestionCountUpDuration - get the duration needed to count up to the questions percentage
 *
 * @param  {number} questionPercentage - the percentage (0 - 100) of questions the students got correct
 *
 * @returns number - the duration of the animation
 */
export function getQuestionCountUpDuration(questionPercentage: number): number {
	return (questionPercentage / 100) * QUESTION_COUNT_UP_TIME_MAX
}

/**
 * getQuestionPageDuration - get the total duration needed to view the question page
 *
 * @param  {?AnalyticsOverview} analyticsOverview - the overview of the analytics for the mission
 *
 * @returns number - the duration needed to display the question page (0 of skipped)
 */
export function getQuestionPageDuration(
	analyticsOverview: ?AnalyticsOverview
): number {
	const questionPercentage = getQuestionPercentage(analyticsOverview)

	if (questionPercentage == null) {
		return 0
	}

	return (
		PAGE_DURATION +
		getQuestionCountUpDuration(questionPercentage) +
		QUESTION_COUNT_UP_DELAY
	)
}

/**
 * getTopScoresPageDuration - get the duration needed to display the top scoring students
 *
 * @param  {?BothFullMissionStates} missionState - the state of the mission
 *
 * @returns number - the duration to display all the top scoring students
 */
export function getTopScoresPageDuration(
	missionState: ?BothFullMissionStates
): number {
	if (!missionState) {
		return 0
	}

	const students = getTopScorers(missionState)

	if (!students) {
		return 0
	}

	return (
		PAGE_DURATION +
		TOP_STUDENT_DURATION_PER_STUDENT * students.length +
		TOP_STUDENT_EXPANDING_DURATION
	)
}

/**
 * getHealthAwardDuration - get the duration of the health award page duration
 *
 * @param  {?AnalyticsStore} eomsData - the end of mission sequence data
 *
 * @returns number - the number of milliseconds the health award page will display for
 */
export function getHealthAwardDuration(eomsData: ?AnalyticsStore): number {
	if (
		eomsData?.analytics?.awards?.every(
			({ type }) => type !== AWARD_TYPES.CLASS_HEALTH
		) ||
		eomsData?.analytics?.averageClassHealth == null
	) {
		return 0
	}
	return PAGE_DURATION + HEALTH_COUNT_UP_DURATION + POINTS_PING_DURATION
}

/**
 * getFullMissionResultDuration - get the total amount of time needed to run the end of mission sequence
 *
 * @param  {?AnalyticsStore} eomsData - the analytics store
 * @param  {?BothFullMissionStates} missionState - the current state of the mission
 *
 * @returns number - the duration (in seconds) needed to run the end of mission sequence
 */
export function getFullMissionResultDuration(
	eomsData: ?AnalyticsStore,
	missionState: ?BothFullMissionStates
): number {
	const overview = getAnalyticsOverview(eomsData?.analytics)
	return Math.ceil(
		(getQuestionPageDuration(overview) +
			getTopScoresPageDuration(missionState) +
			getHealthAwardDuration(eomsData) +
			LEADERBOARD_PAGE_DURATION) /
			ONE_SECOND
	)
}
